/**
 * T076 — App root: wires the session bootstrap to the hand-rolled router.
 *
 * Layout:
 *
 *     <SessionProvider>
 *       <Router>
 *         /                 → <RequireOwner><DashboardPage /></RequireOwner>
 *         /login            → <LoginPage />                      (explicit)
 *         /proposals/:id    → <RequireOwner><ProposalDetailPage /></RequireOwner>
 *         *                 → <NotFoundPage /> (Polish 404)
 *       </Router>
 *     </SessionProvider>
 *
 * `RequireOwner` gates the authenticated shell: while the session boot is in
 * flight it shows the default <StateBoundary> loader (Polish "Ładowanie…"),
 * and when the owner is null it falls back to the login page. The api layer
 * (lib/api.ts) already hard-redirects to `/auth/google` on a 401 and throws
 * the `redirecting` sentinel, so rendering <LoginPage /> here is a
 * flash-prevention net rather than a primary code path.
 *
 * A raw `<LoginPage />` is ALSO reachable via `/login`; this is what the
 * backend's non-owner 302 (`/login?error=not_owner`) lands on. LoginPage
 * reads the query param and renders the Polish 403 banner.
 *
 * T093 — Route-level code-splitting. Each page module is imported via
 * `React.lazy()` so Vite emits a separate chunk per route (dashboard, login,
 * proposal, 404). The Suspense fallback reuses our <StateBoundary loading>
 * primitive so the Polish "Ładowanie…" copy is consistent with every other
 * async surface. See `vite.config.ts` for the `manualChunks` split that
 * isolates `uplot` and the React runtime into their own chunks; combined
 * with the per-route splits below that keeps the initial HTML payload
 * small (uplot alone is ~50 KB gzipped and is only loaded by
 * DashboardPage's PortfolioChart, which itself is now lazy).
 */
import { type ReactElement, type ReactNode, Suspense, lazy } from "react";

import { StateBoundary } from "./components/StateBoundary";
import { Router } from "./lib/router";
import { SessionProvider, useSession } from "./lib/session";

// Route-level code-split: one chunk per page. Each `.then()` maps the named
// export onto the default export shape expected by React.lazy().
const AboutPage = lazy(() => import("./pages/AboutPage").then((m) => ({ default: m.AboutPage })));
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const LoginPage = lazy(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const ProposalDetailPage = lazy(() =>
  import("./pages/ProposalDetailPage").then((m) => ({ default: m.ProposalDetailPage })),
);

/** Shared Suspense fallback — keeps the loader copy uniform across routes. */
const ROUTE_FALLBACK: ReactNode = <StateBoundary loading>{null}</StateBoundary>;

interface RequireOwnerProps {
  children: ReactNode;
}

function RequireOwner({ children }: RequireOwnerProps): ReactNode {
  const { owner, loading } = useSession();
  if (loading) {
    return <StateBoundary loading>{null}</StateBoundary>;
  }
  if (!owner) {
    // api.ts hard-redirects to /auth/google on 401; this renders only during
    // the pre-redirect flash.
    return <LoginPage />;
  }
  return <>{children}</>;
}

export default function App(): ReactElement {
  return (
    <SessionProvider>
      <Suspense fallback={ROUTE_FALLBACK}>
        <Router
          routes={[
            {
              path: "/",
              element: (
                <RequireOwner>
                  <DashboardPage />
                </RequireOwner>
              ),
            },
            {
              path: "/about",
              element: (
                <RequireOwner>
                  <AboutPage />
                </RequireOwner>
              ),
            },
            { path: "/login", element: <LoginPage /> },
            {
              path: "/proposals/:id",
              element: (
                <RequireOwner>
                  <ProposalDetailPage />
                </RequireOwner>
              ),
            },
          ]}
          notFound={<NotFoundPage />}
        />
      </Suspense>
    </SessionProvider>
  );
}
